/**
 * Replace Google place placeholder elements with relevant images
 *
 * @package
 */

/**
 * Internal dependencies
 */
import load from './load';

/**
 * Replace a single element with a Google place image
 *
 * @param {google.maps.places.PlacesService} service The Google place service
 * @param {HTMLElement}                      element The element to replace
 */
const replaceOne = (
	service: google.maps.places.PlacesService,
	element: HTMLElement
) => {
	if ( element.dataset.placeid ) {
		/**
		 * Element has a place ID, so get the image
		 *
		 * @param {google.maps.places.PlaceDetailsRequest} place  The place details request
		 * @param {google.maps.places.PlacesServiceStatus} status The status of the request
		 */
		service.getDetails(
			{ placeId: element.dataset.placeid, fields: [ 'photos' ] },
			function ( place, status ) {
				if (
					status === google.maps.places.PlacesServiceStatus.OK &&
					place
				) {
					if ( place.photos && place.photos.length ) {
						// Place found and has photos, so get the first one

						/**
						 * The image sizes
						 *
						 * @param {google.maps.places.PhotoOptions} sizes
						 */
						const sizes: google.maps.places.PhotoOptions = {};
						if (
							element.dataset.width &&
							Number( element.dataset.width ) > 0
						) {
							// Element has a width, so set the max width
							sizes.maxWidth = Number( element.dataset.width );
						}
						if (
							element.dataset.height &&
							Number( element.dataset.height ) > 0
						) {
							// Element has a height, so set the max height
							sizes.maxHeight = Number( element.dataset.height );
						}

						// Set the image
						element.innerHTML = `<img src="${ place.photos[ 0 ].getUrl(
							sizes
						) }" alt="">`;

						/**
						 * Collect the image attributions
						 *
						 * @member	{string[]}	attributions	The attributions
						 */
						const attributions = (
							place.html_attributions ?? []
						).concat( place.photos[ 0 ].html_attributions ?? [] );

						if ( attributions ) {
							/**
							 * If image has attributions create a citation element and append it
							 *
							 * @member	{HTMLCiteElement}	attribution	The citation element
							 */
							const attribution =
								document.createElement( 'cite' );
							attribution.className =
								'wp-stockist-map-attribution';
							attribution.innerHTML = attributions
								.join( '\n' )
								.replace( /(<a[^>]+)>/, '$1 target="_blank">' );
							element.appendChild( attribution );
						}
					}
				}
			}
		);
	}
};

/**
 * Replace a collection Google place placeholder elements with relevant images
 *
 * @param {HTMLElement|Array<HTMLElement>|NodeListOf<HTMLElement>} elements The elements to replace
 * @param {HTMLDivElement|google.maps.Map|null}                    map      The element to apply attributions to (default: null)
 */
const replace = (
	elements: HTMLElement | Array< HTMLElement > | NodeListOf< HTMLElement >,
	map: HTMLDivElement | google.maps.Map | null = null
) => {
	if ( map === null ) {
		// No map element provided, so create a temporary element
		map = document.createElement( 'div' );
	}

	/**
	 * Load the Google places service
	 *
	 * @param {PlaceService} PlaceService The Google places service
	 */
	load().then( ( PlaceService ) => {
		const service = new PlaceService(
			map as HTMLDivElement | google.maps.Map
		);

		if ( elements instanceof HTMLElement ) {
			// Elements is a single element, so replace it
			replaceOne( service, elements );
		} else {
			/**
			 * Loop through the elements and replace them
			 *
			 * @param {HTMLElement} element The single element
			 */
			elements.forEach( ( element ) => {
				replaceOne( service, element );
			} );
		}
	} );
};

export default replace;
